import { CloseIcon, EditIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  ModalBody,
  ModalFooter,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { DefaultModal } from '@components/Modals'
import {
  Encounter,
  EncounterStatus,
  encounterTypeName,
  MIN_AMENDMENT_REASON_LENGTH,
} from '@hb/shared'
import { useCallback, useState } from 'react'
import { useEncounterStatusLabel } from '../useEncounterStatusLabel'
import { formatEncounterDate } from '../utils'

const statusColorScheme: Record<EncounterStatus, string> = {
  [EncounterStatus.DRAFT]: 'orange',
  [EncounterStatus.COMPLETED]: 'green',
  [EncounterStatus.AUTHENTICATED]: 'blue',
}

// §2.11: Amendment modal — provider must supply a reason before re-opening an
// authenticated note for editing. The reason is passed back so callers can
// write it into data.amendmentReason before the form re-mounts.
const AmendModal = ({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}) => {
  const [reason, setReason] = useState('')
  // §1.6.1 / decision 5: the reason cannot be generic — require a minimum length
  // (also enforced server-side when the amendment is re-authenticated).
  const reasonTooShort = reason.trim().length < MIN_AMENDMENT_REASON_LENGTH

  const handleConfirm = useCallback(() => {
    if (reason.trim().length < MIN_AMENDMENT_REASON_LENGTH) return
    onConfirm(reason.trim())
    setReason('')
  }, [reason, onConfirm])

  const handleClose = useCallback(() => {
    setReason('')
    onClose()
  }, [onClose])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={handleClose}
      header="Amend Authenticated Note"
      render={() => (
        <>
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              {/* §1.6.1 amendment-initiation copy — verbatim; do not editorialize. */}
              <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.300' }}>
                This note has been authenticated. Any changes you make will be recorded as an
                amendment alongside the original text. Your original documentation is preserved and
                will remain visible in the chart. This protects you and your patient.
              </Text>
              <Box>
                <Text fontSize="sm" fontWeight={600} color="gray.700" mb={1}>
                  Reason for amendment *
                </Text>
                <Textarea
                  size="sm"
                  placeholder="Describe why this amendment is needed (required)"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={3}
                  borderRadius={6}
                  borderColor={reasonTooShort ? 'orange.300' : 'gray.200'}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Please describe the specific reason for this amendment (at least{' '}
                  {MIN_AMENDMENT_REASON_LENGTH} characters).
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Flex gap={2} justify="flex-end">
              <Button size="sm" variant="outline" colorScheme="gray" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="orange"
                isDisabled={reasonTooShort}
                onClick={handleConfirm}>
                Proceed
              </Button>
            </Flex>
          </ModalFooter>
        </>
      )}
    />
  )
}

export const EncounterViewHeader = ({
  encounter,
  onClose,
  onEdit,
}: {
  encounter: Encounter
  onClose: () => void
  // For authenticated encounters, onEdit receives the amendment reason so the
  // caller can persist it (e.g. write to data.amendmentReason before form opens).
  onEdit?: (amendmentReason?: string) => void
}) => {
  const isAuthenticated = encounter.status === EncounterStatus.AUTHENTICATED
  const statusLabel = useEncounterStatusLabel(encounter.status)
  const [amendModalOpen, setAmendModalOpen] = useState(false)

  const handleAmendConfirm = useCallback(
    (reason: string) => {
      setAmendModalOpen(false)
      onEdit?.(reason)
    },
    [onEdit],
  )

  return (
    <>
      <Flex
        align="center"
        px={4}
        py={2}
        borderBottom="1px solid #cdcdcd"
        bg="white"
        _dark={{ bg: 'gray.900' }}
        gap={3}
        w="100%"
        flexShrink={0}>
        <Box flex={1} minW={0}>
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text
              fontWeight={600}
              fontSize="lg"
              fontFamily="Comfortaa"
              color="gray.800"
              _dark={{ color: 'gray.100' }}>
              {encounterTypeName[encounter.type]}
            </Text>
            <Badge colorScheme={statusColorScheme[encounter.status]} fontSize="xs" px={2}>
              {statusLabel}
            </Badge>
          </Flex>
          <Text fontSize="sm" color="gray.500">
            {encounter.date ? formatEncounterDate(encounter.date) : '—'}
          </Text>
        </Box>
        {onEdit && isAuthenticated ? (
          // §2.11: Amend button — only for authenticated notes (legal medical record).
          // Draft and Completed notes open directly in full edit mode; no pencil needed.
          <Button
            size="sm"
            variant="outline"
            colorScheme="orange"
            leftIcon={<EditIcon />}
            onClick={() => setAmendModalOpen(true)}>
            Amend
          </Button>
        ) : null}
        <IconButton
          aria-label="Close"
          icon={<CloseIcon boxSize={3} />}
          size="sm"
          variant="ghost"
          onClick={onClose}
        />
      </Flex>

      <AmendModal
        isOpen={amendModalOpen}
        onClose={() => setAmendModalOpen(false)}
        onConfirm={handleAmendConfirm}
      />
    </>
  )
}
